import {FC, useCallback} from 'react';
import {AppLinkFallbackType} from '../../../../applink/AppLinkFallbackType.enum';
import {pagewebUrlMatcher} from '../../../../applink/PagewebUrlMatcher.singleton';
import {AGE_GRADE_TO_NUMBER_MAP} from '../../../../common/constant';
import {FetchStatus} from '../../../../common/FetchStatus.enum';
import {getShouldSelfCertToAccessContent} from '../../../../common/getShouldSelfCertToAccessContent';
import {requestContext} from '../../../../common/requestContext';
import useTypedSelector from '../../../../common/useTypedSelector';
import {Loading} from '../../../../design-system/atoms/Loading/Loading';
import {AppDownload} from '../../../../design-system/organisms/AppDownload/AppDownload';
import {SubtabAgeRestrictError} from '../../../../design-system/organisms/SubtabAgeRestrictError/SubtabAgeRestrictError';
import {SubtabAgeRestrictErrorType} from '../../../../design-system/organisms/SubtabAgeRestrictError/SubtabAgeRestrictErrorType.enum';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {PagewebError} from '../../../../errors/PagewebError.class';
import {
  AgeGrade,
  LayoutType,
  StaticLandingType,
} from '../../../../graphql/generated/types';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {useSelectLayout} from '../../../../pagewebLayout/useSelectLayout';
import {SelfCertRequestType} from '../../../../selfCert/SelfCertRequestType.enum';
import {processSelfCertRequest} from '../../../../user/processSelfCertRequest';
import {useLogin} from '../../../../user/useLogin.hook';
import {CommonLayoutProps} from '../../type';
import {Hidden} from '../Hidden';
import Section from '../Sections';
import {Home} from './Home';
import {Landing} from './Landing';

interface Props {
  id: string;
  className?: string;
}

function Layout({id, className}: Props) {
  const {layout, fetchStatus, error} = useSelectLayout(id);
  const {sectionsInLayout, subtabMap, sections} = useTypedSelector(state => ({
    sectionsInLayout: state.pagewebLayout.relations.layouts[id]?.childrenIds,
    subtabMap: state.menu.entities.subtabMap,
    sections: state.pagewebLayout.entities.sections,
  }));
  const {isLogin, user, doLogin} = useLogin();
  const onClickLogin = useCallback(() => {
    doLogin();
  }, [doLogin]);
  const onClickProcessAuth = useCallback(() => {
    processSelfCertRequest({
      selfCertRequestParams: {
        type: SelfCertRequestType.Age19,
      },
    });
  }, []);

  if (
    fetchStatus === FetchStatus.Loading ||
    fetchStatus === FetchStatus.Idle ||
    !layout
  ) {
    return (
      <div className="flex-center-full">
        <Loading />
      </div>
    );
  }

  const ageGrade = layout.ageGrade;
  if (ageGrade === AgeGrade.Nineteen) {
    if (!isLogin) {
      return (
        <SubtabAgeRestrictError
          isMobile={requestContext.requestInfo.display.isMobile}
          type={SubtabAgeRestrictErrorType.BeforeLogin}
          onClickLogin={onClickLogin}
        />
      );
    } else if (
      !user?.age ||
      getShouldSelfCertToAccessContent({
        ageGrade,
        userAge: user.age,
        userAuthenticatedAt: user.authenticatedAt,
      })
    ) {
      return (
        <SubtabAgeRestrictError
          isMobile={requestContext.requestInfo.display.isMobile}
          type={SubtabAgeRestrictErrorType.NotAuthenticatedUser}
          onClickProcessAuth={onClickProcessAuth}
        />
      );
    } else if (user.age < AGE_GRADE_TO_NUMBER_MAP[ageGrade]) {
      return (
        <SubtabAgeRestrictError
          isMobile={requestContext.requestInfo.display.isMobile}
          type={SubtabAgeRestrictErrorType.Under19}
        />
      );
    }
  }
  if (error) {
    throw new PagewebError(getSerializedPagewebError(error));
  }

  const ChoosedComponent =
    !layout.type || !layoutTypeComponentMap[layout.type]
      ? Hidden
      : layoutTypeComponentMap[layout.type];

  return (
    <ChoosedComponent
      layout={layout}
      className={className}
      trackEventObj={{
        customProps: {section_cnt: String(sectionsInLayout?.length)},
      }}
    >
      {sectionsInLayout?.map((sectionId, index) => (
        <div
          key={`section-${index}`}
          className="mb-4pxr flex-col"
          {...putTiaraObjectProps({
            click: {setnum: String(index + 1)},
            viewimp_contents: {imp_area_ordnum: String(index + 1)},
            customProps: {
              section_id:
                sections[sectionId].uid !== undefined
                  ? String(sections[sectionId].uid)
                  : undefined,
              section_cnt: String(sectionsInLayout.length),
            },
          })}
        >
          <Section id={sectionId} />
        </div>
      ))}
      {layout.type === LayoutType.Layout && (
        <AppDownload
          isMobile={requestContext.requestInfo.display.isMobile}
          isFullBannerOnPc={
            // 서브탭 중 요일, 무료의 경우 패딩이 없는 앱다운로드가 들어가야 합니다.
            layout.screenUid
              ? subtabMap[layout.screenUid]?.landingType ===
                  StaticLandingType.DayOfWeek ||
                subtabMap[layout.screenUid]?.landingType ===
                  StaticLandingType.FreeSeries
              : false
          }
          onClickAppLink={() =>
            window.location.assign(
              pagewebUrlMatcher.getCurrentAppLink({
                schemePrefixType: 'applink',
                fallbackType: AppLinkFallbackType.Store,
              })
            )
          }
        />
      )}
    </ChoosedComponent>
  );
}

const layoutTypeComponentMap: {
  [key in LayoutType]: FC<CommonLayoutProps>;
} = {
  [LayoutType.Layout]: Home,
  [LayoutType.Landing]: Landing,
};

export default Layout;
